import {
  ActionIcon,
  BackgroundImage,
  Box,
  Button,
  Card,
  Center,
  Flex,
  Group,
  Image,
  Loader,
  Pagination,
  Space,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconChevronDown,
  IconChevronUp,
  IconDownload,
} from "@tabler/icons-react";
import LoanResultHero, {
  LoanResultState,
  loanResultMessage,
} from "@/modules/credit/loan-management/LoanResultHero";
import FileBadge from "@/modules/transfer-money/transfer-international/components/FileBadge";
import background from "assets/images/pngs/background.png";
import image11 from "@/assets/images/pngs/image11.png";
import { useLocation, useNavigate } from "react-router-dom";
import RoutesString from "@/pages/routesString";
import Img14 from "@/assets/images/pngs/image14.png";
import Img19 from "@/assets/images/pngs/image19.png";
import ImgReturn from "@/assets/images/pngs/img-return.png";
import {
  useGetTransactionDetailQuery,
  useGetBatchOrderDetailsQuery,
} from "@/services/transferAPI";
import { useGetDebitCardOverviewQuery } from "@/services/cardAPI";
import { get } from "lodash";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { ReactNode, useState } from "react";
import { formatCurrency } from "@/utils/common";
import ListOrder from "@/modules/transfer-money/transfer-by-batch/components/list-order/ListOrder";
import { useTranslation } from "react-i18next";
import { pickSavingsBannerAmount } from "./savingsBanner";
import i18n from "i18n/i18n";
import { beText } from "@/utils/beText";
import { openStoredDocument } from "@/utils/openDocument";

const NAVY = "#104783";

const Row = ({ label, value }: { label: string; value: ReactNode }) => (
  <Flex
    justify="space-between"
    py={8}
    style={{ borderBottom: "1px dashed #E5E7EB" }}
  >
    <Text c="dimmed">{label}</Text>
    <Text fw={500} c="#121826" ta="right">
      {value}
    </Text>
  </Flex>
);

const AMBER = { color: "#F47920", bg: "#FFF3E8", border: "#FCE0C7" };
const GREEN = { color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" };
const RED = { color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" };
const GRAY = { color: "#525866", bg: "#F4F4F6", border: "#E5E7EB" };

const getBatchStatus = (): Record<string, { label: string } & typeof AMBER> => ({
  INIT: { label: i18n.t("TRANSFER_STEP_ONE_LABEL"), ...GRAY },
  CREATE: { label: i18n.t("WAITING_APPROVE"), ...AMBER },
  WAITING_APPROVE: { label: i18n.t("WAITING_APPROVE"), ...AMBER },
  WAITING: { label: i18n.t("WAITING_APPROVE"), ...AMBER },
  PENDING: { label: i18n.t("PENDING"), ...AMBER },
  APPROVING: { label: i18n.t("APPROVING"), ...AMBER },
  PROCESSING: { label: i18n.t("APPROVING"), ...AMBER },
  SUCCESS: { label: i18n.t("SUCCESS"), ...GREEN },
  FAIL: { label: i18n.t("FAILED"), ...RED },
  FAILED: { label: i18n.t("FAILED"), ...RED },
  REJECTED: { label: i18n.t("REJECT"), ...RED },
  RETURNED: { label: i18n.t("HAS_BEEN_RETURNED"), ...GRAY },
  RETURN: { label: i18n.t("HAS_BEEN_RETURNED"), ...GRAY },
  CANCELLED: { label: i18n.t("CANCELLED"), ...GRAY },
});

const StatusPill = ({ status }: { status?: string }) => {
  const key = (status || "").toUpperCase();
  const s = getBatchStatus()[key] || { label: status || "-", ...GRAY };
  return (
    <Text
      span
      fz={13}
      fw={600}
      style={{
        color: s.color,
        backgroundColor: s.bg,
        border: `1px solid ${s.border}`,
        borderRadius: 20,
        padding: "2px 12px",
      }}
    >
      {s.label}
    </Text>
  );
};

dayjs.extend(customParseFormat);

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  // Backend trả "DD/MM/YYYY HH:mm:ss.SSS" — parse đúng định dạng, tránh dayjs
  // hiểu nhầm sang MM/DD/YYYY (kiểu Mỹ) làm đảo ngày/tháng.
  const d = dayjs(value, [
    "DD/MM/YYYY HH:mm:ss.SSS",
    "DD/MM/YYYY HH:mm:ss",
    "DD/MM/YYYY HH:mm",
    "HH:mm DD/MM/YYYY",
  ]);
  if (!d.isValid()) return value;
  // WORKAROUND: makerDate/actionTime bị cộng dư +7h (Jackson time-zone
  // Asia/Ho_Chi_Minh áp lên instant vốn đã là giờ local) → trừ lại 7h để về
  // đúng giờ thực. Bỏ dòng subtract này khi backend sửa đúng múi giờ.
  // JVC-6807: mockup dùng "DD/MM/YYYY HH:mm" (ngày trước giờ), không phải "HH:mm DD/MM/YYYY".
  return d.subtract(7, "hour").format("DD/MM/YYYY HH:mm");
};

// Tô màu badge trạng thái theo nhãn tiếng Việt (detailObjectList trả label sẵn).
const statusColorByLabel = (label: string) => {
  const l = (label || "").toLowerCase();
  if (/thành công|đã duyệt|hoàn tất|chấp nhận/.test(l)) return GREEN;
  if (/từ chối|thất bại|huỷ|hủy/.test(l)) return RED;
  if (/chờ|đang/.test(l)) return AMBER;
  return GRAY;
};

const LabelStatusPill = ({ label }: { label?: string }) => {
  const s = statusColorByLabel(label || "");
  return (
    <Text
      span
      fz={13}
      fw={600}
      style={{
        color: s.color,
        backgroundColor: s.bg,
        border: `1px solid ${s.border}`,
        borderRadius: 20,
        padding: "2px 12px",
      }}
    >
      {label || "-"}
    </Text>
  );
};

// Nhận diện dòng "Trạng thái" trong detailObjectList để render badge thay vì text.
const isStatusKey = (key?: string) => /trạng thái|trang thai|status/i.test(key || "");

// Suy đuôi file từ tên file (documentName) → truyền cho FileBadge.
const getFileExt = (name?: string) => {
  if (!name) return "";
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1) : "";
};

// JVC-3526: banner cho các loại GD khác (vd Hủy liên kết thẻ) theo hành động duyệt —
// từ chối phải hiển thị "đã bị từ chối", không phải "đã được phê duyệt".
// JVC-3520: kèm tên loại giao dịch theo thiết kế, vd
// "Yêu cầu thiết lập hạn mức giao dịch thẻ đã được chấp nhận".
const getOtherBannerText = (mode?: string, transactionTypeLabel?: string) => {
  // Chuẩn hóa label ("Hủy Liên Kết Thẻ Với Javis Biz" -> "hủy liên kết thẻ với Javis Biz")
  // để ghép câu banner tự nhiên; giữ nguyên tên riêng "Javis Biz".
  const type = transactionTypeLabel
    ? ` ${transactionTypeLabel.toLowerCase().replace(/javis biz/gi, "Javis Biz")}`
    : "";
  switch (mode) {
    case "REJECT":
      return i18n.t("TPL_REQUEST_REJECTED", { p0: type });
    case "RETURN":
      return i18n.t("TPL_REQUEST_RETURNED", { p0: type });
    case "APPROVE":
    default:
      return i18n.t("TPL_REQUEST_ACCEPTED", { p0: type });
  }
};

// Text banner theo hành động của lô
const getBulkBannerText = (mode?: string) => {
  switch (mode) {
    case "APPROVE":
      return i18n.t("BATCH_TRANSFER_REQUEST_ACCEPTED");
    case "RETURN":
      return i18n.t("BATCH_TRANSFER_REQUEST_RETURNED");
    case "REJECT":
      return i18n.t("BATCH_TRANSFER_REQUEST_REJECTED");
    default:
      return i18n.t("BATCH_TRANSFER_REQUEST");
  }
};

const ApprovelLockAccountChecker = () => {
  const { t } = useTranslation();

  const { state } = useLocation();
  const navigate = useNavigate();
  console.log("stateChecker", state);

  const detailRequest = state?.detailRequest;
  const isTransfer = state?.type === "TRANSFER";
  const isBulk = state?.type === "BULK_TRANSFER";
  const isLockAccount = state?.type === "LOCK_ACCOUNT";
  const isSavings = state?.type === "SAVINGS";
  // Khoá tài khoản: không có chứng từ -> ẩn mục "Danh sách file chứng từ".
  const isCloseAccount = state?.type === "CLOSE_ACCOUNT";
  // GD liên kết/hủy liên kết thẻ: theo figma chỉ hiển thị 6 field.
  const isCardLink = [
    "DEBIT_CARD_LINK_TO_BIZ",
    "CREDIT_CARD_LINK_TO_BIZ",
    "DEBIT_CARD_UNLINK_FROM_BIZ",
    "CREDIT_CARD_UNLINK_FROM_BIZ",
  ].includes(String(state?.type));
  /**
   * #56046: giao dịch tác động lên tài khoản tiền gửi (tất toán, rút gốc từng phần, nạp thêm).
   * `detailObjectList` của các loại này gần như rỗng — mapper chung chỉ đọc được trường của giao
   * dịch chuyển tiền — nên màn kết quả chỉ còn đúng dòng "Trạng thái duyệt". Dữ liệu thật nằm ở
   * `transactionDetail.savingsTransaction` / `.transactionInfo` / `.beneficiaryAccount`.
   */
  const isSavingsAction = [
    "DEPOSIT_SETTLEMENT",
    "PARTIAL_WITHDRAW",
    "DEPOSIT_MANUAL_TOP_UP",
  ].includes(String(state?.type));
  /** Các loại giao dịch chưa có UI riêng — render fallback theo key-value. */
  const isOtherType = !isTransfer && !isBulk && !isLockAccount && !isSavings;
  /**
   * Sau khi confirmAction thành công, call lại detail để màn hoàn thành luôn
   * hiển thị thông tin mới nhất từ BE — không tin cache, không dùng dataConfirm
   * khi có detailRequest hợp lệ.
   */
  const { data: detailData, isFetching: isFetchingDetail } =
    useGetTransactionDetailQuery(detailRequest, {
      skip: !detailRequest?.transId,
      refetchOnMountOrArgChange: true,
    });

  // GD liên kết/hủy liên kết thẻ: Số thẻ/Chủ thẻ trong detailObjectList có thể rỗng
  // (BE tạo từ MOBILE không điền) -> lấy cardId từ approvalInfoDetail.information rồi
  // gọi getDebitCardOverview để lấp Số thẻ (cardNo) + Chủ thẻ (cardHolderName).
  const cardOverviewId = (() => {
    if (!isCardLink) return undefined;
    try {
      const info = get(detailData, "body.approvalInfoDetail.information");
      const n = Number(JSON.parse(String(info || "{}"))?.cardId);
      return Number.isNaN(n) ? undefined : n;
    } catch {
      return undefined;
    }
  })();
  const { data: cardOverviewData } = useGetDebitCardOverviewQuery(
    { cardId: cardOverviewId as number },
    { skip: !cardOverviewId },
  );
  const cardOverview = get(cardOverviewData, "body", {});

  /** Ưu tiên detailObjectList từ response detail (fresh), fallback dataConfirm. */
  const detailObjectList: Array<{
    key: string;
    name?: string;
    display?: string;
    value: string;
  }> =
    get(detailData, "body.transactionDetailObject.detailObjectList") ||
    state?.dataConfirm?.transactionDetailObject?.detailObjectList ||
    [];
  // JVC-3645: loại dòng có nhãn là KEY tiếng Anh thô (vd "fromAccountNo",
  // "fromAccountFullName") lọt ra từ detailObjectList — nhãn thật của UI luôn có dấu/space,
  // key thô là camelCase liền không dấu.
  const isRawEnglishKey = (label?: string) =>
    /^[a-z][a-zA-Z0-9]*$/.test(String(label || "").trim());
  // JVC-3530/3536: GD thẻ bỏ dòng không có giá trị (vd Người tạo bị BE ẩn sau khi duyệt,
  // Người duyệt khi chưa duyệt) — thiết kế không hiển thị dòng trống.
  // JVC-3645: chuyển tiền cũng lọc dòng rỗng + bỏ field key tiếng Anh thô.
  const otherDetailList =
    isOtherType || isTransfer
      ? detailObjectList.filter(
          (it) =>
            it?.value !== null &&
            it?.value !== undefined &&
            String(it.value).trim() !== "" &&
            !isRawEnglishKey(it?.display || it?.name || it?.key),
        )
      : detailObjectList;
  // GD liên kết/hủy liên kết thẻ: whitelist đúng 6 field figma (bỏ Loại thẻ, Số tài khoản,
  // Liên kết với Javis Biz, Ghi chú, Trạng thái duyệt mà BE trả dư), relabel theo figma.
  const normKey = (s?: string) =>
    String(s ?? "")
      .replace(/:$/, "")
      .trim()
      .toLowerCase();
  const pickCardValue = (keys: string[]) => {
    const match = keys
      .map((k) =>
        detailObjectList.find(
          (it) => normKey(it.key) === normKey(k) || normKey(it.name) === normKey(k),
        ),
      )
      .find((found) => found && String(found.value ?? "").trim() !== "");
    return match?.value ?? "";
  };
  // Người duyệt/Ngày duyệt: detailObjectList trả rỗng -> backfill từ log hành động (APPROVE/
  // REJECT/RETURN) trong transactionLogInfo, fallback approvalInfoDetail.createdBy/createdAt.
  const cardLogList: any[] = get(detailData, "body.transactionLogInfo", []);
  const cardActionLog =
    cardLogList.find((l) => l?.actionCode === state?.mode) ||
    cardLogList.find((l) => l?.approvalUserName) ||
    cardLogList[0];
  const fmtActionDate = (v?: string) => {
    // actionTime BE dạng "DD/MM/YYYY HH:mm:ss.SSS" đã là giờ local (khớp createdAt hiển thị)
    // -> KHÔNG trừ 7h; format "HH:mm DD/MM/YYYY".
    const d = dayjs(String(v ?? ""), [
      "DD/MM/YYYY HH:mm:ss.SSS",
      "DD/MM/YYYY HH:mm:ss",
      "DD/MM/YYYY HH:mm",
    ]);
    return d.isValid() ? d.format("HH:mm DD/MM/YYYY") : String(v ?? "");
  };
  const cardApprover =
    pickCardValue([t("REVIEWER"), t("INITIATOR")]) ||
    get(cardActionLog, "approvalUserName", "") ||
    get(detailData, "body.approvalInfoDetail.createdBy", "");
  const cardApprovalDate =
    pickCardValue([t("APPROVAL_DATE"), t("DATE_CREATED")]) ||
    fmtActionDate(get(cardActionLog, "actionTime", "")) ||
    get(detailData, "body.approvalInfoDetail.createdAt", "");
  const cardLinkDetailList = [
    { key: t("REFERENCE_ID"), value: pickCardValue([t("REFERENCE_ID"), t("TRANSACTION_CODE")]) },
    { key: t("TRANSFER_TYPE"), value: pickCardValue([t("TRANSFER_TYPE")]) },
    {
      key: t("CARD_NUMBER"),
      value: pickCardValue([t("CARD_NUMBER")]) || get(cardOverview, "cardNo", ""),
    },
    {
      key: t("CARDHOLDER"),
      value:
        pickCardValue([t("CARDHOLDER"), t("PRODUCT_CARD_HOLER_NAME")]) ||
        get(cardOverview, "cardHolderName", "") ||
        get(detailData, "body.accountInfo.custName", ""),
    },
    { key: t("REVIEWER"), value: cardApprover },
    { key: t("APPROVAL_DATE"), value: cardApprovalDate },
  ];
  const renderDetailList = isCardLink ? cardLinkDetailList : otherDetailList;
  // JVC-3520: tên loại giao dịch cho banner (vd "Thiết lập hạn mức giao dịch thẻ") —
  // lấy từ chính dòng "Loại giao dịch" trong detailObjectList (config-driven, khỏi hardcode map).
  const otherTransactionTypeLabel = otherDetailList.find(
    (it) =>
      /^loại giao dịch$/i.test(String(it?.key || "")) ||
      (it as { name?: string })?.name === "transactionType",
  )?.value;

  /** Danh sách chứng từ đính kèm (ưu tiên response detail, fallback dataConfirm). */
  const documentInfoList: Array<{
    documentId?: number;
    documentName?: string;
    documentPath?: string;
  }> =
    get(detailData, "body.documentInfoList") ||
    get(detailData, "body.transactionDetail.documentInfoList") ||
    state?.dataConfirm?.documentInfoList ||
    [];

  // Dữ liệu cấu trúc cho giao dịch chuyển tiền theo lô (render theo Figma)
  const txDetail: any = get(detailData, "body.transactionDetail", {});
  const txLogInfo: any[] = get(detailData, "body.transactionLogInfo", []);
  // Ưu tiên bản ghi APPROVE; nếu chưa có (đang chờ duyệt) thì lấy log gần nhất có người duyệt/tạo
  const approveLog =
    txLogInfo.find((l) => l?.actionCode === "APPROVE") ||
    txLogInfo.find((l) => l?.approvalUserName) ||
    txLogInfo[0];
  // "Ngày khởi tạo" lấy từ log CREATE (makerDate/actionTime — đúng giờ local),
  // KHÔNG dùng txDetail.transactionDate vì backend trả field này lệch +7h.
  const createLog =
    txLogInfo.find((l) => l?.actionCode === "CREATE") || txLogInfo[0];
  const createdDate =
    createLog?.makerDate || createLog?.actionTime || txDetail?.transactionDate;
  // JVC-3534 (debit) & JVC-3532 (credit): màn kết quả khóa/mở khóa thẻ cần hiển thị
  // "Người duyệt/Ngày duyệt" (không có trong detailObjectList do BE trả) → lấy từ log APPROVE trên FE.
  const businessType: string =
    get(detailData, "body.approvalInfoDetail.businessType") ||
    get(detailData, "body.transactionDetail.transactionType") ||
    "";
  const isCardLockAction = /(DEBIT|CREDIT)_CARD_(LOCK|UNLOCK)/i.test(businessType);
  // ── Khoản vay: hero + bố cục theo bản design 11/08/2026 ────────────────────────────────
  // (document/22-08-2026/giao-dien/GiaoDien_QuanLyKhoanVay_1108updated (1).html)
  // Chỉ áp cho LOAN_CLOSURE/LOAN_PAYMENT — các loại GD khác giữ nguyên banner cũ vì phần lớn
  // không có số tiền để hiện nổi bật.
  const isLoanTx = /^LOAN_(CLOSURE|PAYMENT)$/i.test(businessType);
  /** Đọc 1 dòng trong detailObjectList theo nhãn tiếng Việt do historical-service sinh ra. */
  const loanRow = (label: string) =>
    renderDetailList.find((it) => {
      const l =
        (it as { display?: string })?.display || (it as { name?: string })?.name || it?.key;
      return normKey(l) === normKey(label);
    })?.value;
  /**
   * requestStatusVn() của LoanTransactionDetail (historical-service) → trạng thái bảng design.
   *
   * Trước đây phải lái "đã phê duyệt" sang SUCCESS để màn hiện đúng câu "Ngân hàng đã hạch toán".
   * Nay BE đã xét cả EXECUTION_STATUS nên giao dịch ghi sổ xong trả thẳng "Thành công" — không
   * cần lái nữa, và "đã phê duyệt" trở về đúng nghĩa: đã duyệt mà CHƯA ghi sổ.
   */
  const LOAN_STATE_BY_VN: Record<string, LoanResultState> = {
    "chờ duyệt": "PENDING",
    "đã phê duyệt": "APPROVED",
    "chờ ngân hàng duyệt": "SENT",
    "đang xử lý": "SENT",
    "đang tra soát": "SENT",
    "thành công": "SUCCESS",
    "đã tất toán": "SUCCESS",
    "từ chối": "REJECTED",
    "ngân hàng từ chối": "REJECTED",
    "thất bại": "FAILED",
  };
  const loanState: LoanResultState =
    // eslint-disable-next-line no-nested-ternary
    state?.mode === "RETURN"
      ? "PENDING" // design không có trạng thái "hoàn lại" — yêu cầu quay về chờ duyệt
      : // eslint-disable-next-line no-nested-ternary
      state?.mode === "REJECT"
      ? "REJECTED"
      : LOAN_STATE_BY_VN[normKey(String(loanRow(t("STATUS")) ?? ""))] ?? "APPROVED";
  const loanNoun = /tất toán/i.test(String(loanRow(t("TRANSFER_TYPE")) ?? ""))
    ? t("SETTLEMENT_LOWER")
    : t("PAYMENT_LOWER");
  const loanMessage =
    state?.mode === "RETURN"
      ? getOtherBannerText("RETURN", otherTransactionTypeLabel)
      : loanResultMessage(loanState, loanNoun);
  // BE đã định dạng sẵn kèm "VND" (LoanTransactionDetail#money).
  const loanAmountText = loanRow(t("PAYMENT_AMOUNT"));
  /**
   * JVC-6807: màn kết quả khoản vay bỏ 2 dòng "Mã lệnh gửi ngân hàng" / "Thời điểm gửi ngân hàng"
   * (thừa theo design) và cắt chức danh trong ngoặc sau "Người lập" (vd "… (Giám đốc)"). Chỉ áp cho
   * khoản vay; các loại GD khác giữ nguyên detailObjectList.
   */
  /**
   * OP-55704: mã trạng thái tiếng Anh → tiếng Việt cho dòng "Trạng thái".
   *
   * Ở nhánh Hoàn lại, `detailObjectList` của historical-service trả thẳng mã máy `RETURNED` —
   * người duyệt nhìn thấy đúng chữ đó trên màn kết quả. Nhánh Từ chối thì BE đã dịch sẵn nên
   * không lộ. Map ở đây thay vì sửa BE để màn kết quả không phụ thuộc việc mã lạ nào lọt ra.
   */
  const LOAN_STATUS_VN: Record<string, string> = {
    // Người kiểm thử chốt 09/09: "Hoàn lại", KHÔNG phải "Đã hoàn lại". Cố ý lệch với
    // LOAN_STATUS ở đầu file (vẫn "Đã hoàn lại") — bảng đó là badge của giao dịch THẺ, không
    // dùng cho màn khoản vay (nhánh isLoanTx render Text thường, không render pill).
    RETURNED: t("RETURNED"),
    REJECTED: t("REJECT"),
    RECALLED: t("WITHDRAWN"),
    PENDING: t("WAITING_APPROVE"),
    APPROVED: t("APPROVED"),
    EXPIRED: t("EXPIRED"),
  };

  /** Nhãn dòng lý do theo đúng hành động — design ghi "Lý do từ chối" (OP-55704). */
  const loanReasonLabel =
    state?.mode === "REJECT" ? t("REASON_FOR_REJECTION") : t("REASON_FOR_RETURN");

  /**
   * Màu chữ của giá trị từng dòng ở màn kết quả khoản vay (OP-55704).
   * #56050: dòng "Lý do từ chối" ở màn tiền gửi cũng tô đỏ như khoản vay.
   */
  const loanRowColor = (label?: string): string => {
    if (normKey(String(label ?? "")) === normKey(loanReasonLabel)) return "#E5484D";
    if (!isLoanTx) return "#121826";
    if (normKey(String(label ?? "")) === normKey(t("PAYMENT_AMOUNT"))) return "#2A81D0";
    return "#121826";
  };

  const loanDisplayList = !isLoanTx
    ? renderDetailList
    : renderDetailList
        .filter((it) => {
          const l = normKey(
            String((it as { display?: string }).display ?? (it as { name?: string }).name ?? it?.key ?? ""),
          );
          return (
            l !== normKey(t("BANK_ORDER_CODE")) &&
            l !== normKey(t("TIME_SENT_TO_BANK"))
          );
        })
        .map((it) => {
          const l = normKey(
            String((it as { display?: string }).display ?? (it as { name?: string }).name ?? it?.key ?? ""),
          );
          // OP-55704: dịch mã trạng thái tiếng Anh còn lọt ra (vd RETURNED ở nhánh Hoàn lại).
          if (isStatusKey(l)) {
            const raw = String((it as { value?: string }).value ?? "").trim();
            const vn = LOAN_STATUS_VN[raw.toUpperCase()];
            return vn ? { ...it, value: vn } : it;
          }
          if (l !== normKey(t("PREPARED_BY"))) return it;
          return {
            ...it,
            value: String((it as { value?: string }).value ?? "").replace(/\s*\([^)]*\)\s*$/, "").trim(),
          };
        })
        // OP-55704: design có dòng "Lý do từ chối" (chữ ĐỎ) ngay dưới "Trạng thái"; màn đang
        // thiếu hẳn. Lý do do chính người duyệt vừa nhập nên lấy từ `state`, không chờ BE trả về
        // — màn này hiện ngay sau khi confirm, `detailObjectList` chưa kịp có dòng đó.
        .concat(
          (state?.mode === "REJECT" || state?.mode === "RETURN") && state?.reason
            ? [{ key: loanReasonLabel, value: state.reason }]
            : [],
        );
  /**
   * Dòng chi tiết cho màn kết quả giao dịch TIỀN GỬI.
   *
   * #56005/#56050: nhánh này trước đây đọc thẳng `state.dataConfirm` — ảnh chụp
   * lúc vừa confirm nên thiếu dòng, và không lọc dòng rỗng như các nhánh khác.
   * Nay dùng chung `detailObjectList` (ưu tiên dữ liệu tươi từ getTransactionDetail,
   * fallback dataConfirm), lọc dòng rỗng, và bơm "Lý do" từ `state` giống khoản vay
   * vì lý do do chính người duyệt vừa nhập, BE chưa kịp trả về.
   */
  const needReasonRow =
    (state?.mode === "REJECT" || state?.mode === "RETURN") && !!state?.reason;
  const reasonRow = { key: loanReasonLabel, value: String(state?.reason ?? "") };
  const savingsDetailRows = detailObjectList
    .filter(
      (it) =>
        it?.value !== null &&
        it?.value !== undefined &&
        String(it.value).trim() !== "" &&
        !isRawEnglishKey(it?.display || it?.name || it?.key),
    )
    .reduce<typeof detailObjectList>((acc, row) => {
      acc.push(row);
      // #56005/#56050: design xếp dòng "Lý do" NGAY DƯỚI dòng trạng thái duyệt,
      // trước đây nối vào cuối danh sách nên tester báo sai thứ tự.
      if (needReasonRow && isStatusKey(row?.display || row?.name || row?.key)) {
        acc.push(reasonRow);
      }
      return acc;
    }, []);
  // Dự phòng: BE có thể không trả dòng trạng thái — khi đó vẫn phải thấy lý do,
  // thà đặt cuối còn hơn mất hẳn.
  if (needReasonRow && !savingsDetailRows.includes(reasonRow)) {
    savingsDetailRows.push(reasonRow);
  }

  const isCardUnlockAction = /(DEBIT|CREDIT)_CARD_UNLOCK/i.test(businessType);
  const approverName =
    approveLog?.approvalUserName ||
    (get(detailData, "body.approvalInfo.approvalUserNames") || []).join(", ") ||
    "-";
  const approvalDate = formatDateTime(approveLog?.actionTime);

  /**
   * Dòng chi tiết màn kết quả giao dịch tiền gửi (#56046) — dựng theo đúng thứ tự bản thiết kế.
   * Ba dòng lãi suất / tiền lãi / phí lấy nguyên nhãn backend đã dịch sẵn trong `transactionInfo`,
   * nên thêm loại giao dịch mới chỉ cần thêm MobileConfig, không phải sửa ở đây.
   *
   * Khai ở ĐÂY chứ không cạnh `renderDetailList`: khối này cần `approverName`, `approvalDate`,
   * `needReasonRow` — đều khai báo phía trên nhưng sau chỗ đó.
   */
  // #56134: xem savingsBanner.ts — vì sao không đọc state.dataConfirm nữa.
  const savingsBannerAmount = pickSavingsBannerAmount(detailData);

  const savingsActionRows = (() => {
    const st: any = get(detailData, "body.transactionDetail.savingsTransaction", {});
    const ben: any = get(detailData, "body.transactionDetail.beneficiaryAccount", {});
    const txInfo = (get(detailData, "body.transactionDetail.transactionInfo", []) ||
      []) as Array<{ key?: string; name?: string; value?: string }>;
    return [
      { key: t("REFERENCE_CODE"), value: st?.refId },
      { key: t("TRANSACTION_TYPE"), value: st?.transactionTypeDisplay },
      {
        key: t("SAVINGS_ACCOUNT_NUMBER"),
        value: get(detailData, "body.approvalInfoDetail.fromAccountNo", ""),
      },
      ...txInfo.map((it) => ({ key: it?.name || it?.key, value: it?.value })),
      { key: t("TOTAL_AMOUNT_RECEIVED"), value: st?.totalAmountDisplay },
      { key: t("BENEFICIARY_ACCOUNT"), value: ben?.accountNo },
      ...(needReasonRow ? [{ key: loanReasonLabel, value: state?.reason }] : []),
      { key: t("REVIEWER"), value: approverName },
      { key: t("APPROVAL_DATE"), value: approvalDate },
    ]
      .map((r) => ({ key: String(r.key ?? ""), value: String(r.value ?? "") }))
      .filter((r) => r.key.trim() !== "" && r.value.trim() !== "" && r.value !== "-");
  })();

  /** Màn kết quả dùng danh sách riêng cho giao dịch tiền gửi, còn lại giữ nguyên đường cũ. */
  const displayRows = isSavingsAction ? savingsActionRows : loanDisplayList;
  // amount/feeAmount từ BE có thể là chuỗi có dấu phẩy ("24,010,878") -> bỏ phẩy trước khi parse
  const parseAmount = (v?: string) =>
    parseFloat(String(v ?? "").replace(/[^\d.-]/g, "")) || 0;
  const principalAmount = parseAmount(txDetail?.amount);
  const feeAmount = parseAmount(txDetail?.feeAmount);
  // Tổng tiền thanh toán: ưu tiên totalAmount do BE tính (đã xét feeType — DEDUCT không cộng phí).
  // Fallback principalAmount + feeAmount cho dữ liệu cũ chưa có totalAmount.
  const batchAmount =
    txDetail?.totalAmount != null && txDetail?.totalAmount !== ""
      ? parseAmount(txDetail?.totalAmount)
      : principalAmount + feeAmount;

  // "Xem thêm" -> hiện bảng chi tiết giao dịch của lô
  const [showTable, setShowTable] = useState(false);
  const [batchPage, setBatchPage] = useState(1);
  const { data: batchData, isFetching: isFetchingBatch } =
    useGetBatchOrderDetailsQuery(
      { batchId: txDetail?.batchId, pageNo: batchPage, pageSize: 10 },
      { skip: !isBulk || !txDetail?.batchId },
    );
  const batchItems = get(batchData, "body.items", null);
  const batchTotal = get(batchData, "body.totalInit", 0);
  const batchPages = batchTotal ? Math.ceil(batchTotal / 10) : 1;

  // Khoá tài khoản: dựng 7 field giống màn maker (LockDetailWrapper) — ưu tiên
  // dataConfirm (response confirm), fallback sang dữ liệu detail refetch.
  const dc: any = state?.dataConfirm || {};
  const closeApproversRaw =
    dc?.approvalProcessInfo?.approvalUserNames ??
    get(detailData, "body.approvalInfo.approvalUserNames") ??
    [];
  const closeAccountData = {
    reference:
      dc?.approvalProcessInfo?.approvalProcessId ??
      get(detailData, "body.approvalInfo.approvalProcessId") ??
      get(detailData, "body.approvalInfoDetail.approvalCode") ??
      "-",
    accountName:
      dc?.customerName ??
      get(detailData, "body.accountInfo.custName") ??
      get(detailData, "body.transactionDetail.accountInfo.custName") ??
      "-",
    accountNumber:
      dc?.accountNumber ??
      get(detailData, "body.accountInfo.accountNo") ??
      get(detailData, "body.transactionDetail.accountInfo.accountNo") ??
      "-",
    level:
      dc?.approvalProcessInfo?.numberOfApprovals ??
      get(detailData, "body.approvalInfo.numberOfApprovals") ??
      get(detailData, "body.approvalInfo.totalApprovalLevel") ??
      "-",
    approvers: Array.isArray(closeApproversRaw)
      ? closeApproversRaw.join(", ")
      : closeApproversRaw || "-",
    requestDate:
      dc?.createdAt ??
      get(detailData, "body.approvalInfoDetail.createdAt") ??
      "-",
  };

  if (!state) return null;

  const getImgByType = (type: string) => {
    switch (type) {
      case "LOCK_ACCOUNT":
        return image11;
      case "APPROVE":
        return Img14;
      case "REJECT":
        return Img19;
      case "RETURN":
        return ImgReturn;
      default:
        return image11;
    }
  };

  /**
   * Câu banner của màn kết quả giao dịch TIỀN GỬI, theo đúng hành động vừa thực hiện.
   * #56050: trước đây gán cứng bản "đã được chấp nhận" nên màn từ chối vẫn ghi accepted.
   */
  const getSavingsBannerText = (mode?: string) => {
    switch (mode) {
      case "REJECT":
        return t("DEPOSIT_ACCOUNT_OPENING_CONTRACT_HAS_BEEN_REJECTED");
      case "RETURN":
        return t("DEPOSIT_ACCOUNT_OPENING_CONTRACT_HAS_BEEN_REFUNDED");
      default:
        return t("DEPOSIT_ACCOUNT_OPENING_CONTRACT_ACCEPTED");
    }
  };

  const getStatusTextByMode = (mode: string) => {
    switch (mode) {
      case "APPROVE":
        return t("TRANSACTION_SUCCESS");
      case "REJECT":
        return t("TRANSACTION_FAILED");
      case "RETURN":
        return t("TRANSACTION_REFUNDED");
      default:
        return "";
    }
  };
  return (
    <>
      {/* Sau khi đã duyệt/từ chối/hoàn lại, màn này là KẾT QUẢ chứ không còn là màn tra soát —
          design không có tiêu đề "Xem chi tiết giao dịch" lẫn mũi tên quay lại ở bước đó.
          #56005/#56050: trước đây chỉ áp cho khoản vay, nhưng tiền gửi cũng là màn kết quả
          nên tester báo thừa — nay áp cho mọi loại giao dịch khi đã có `state.mode`. */}
      {!state?.mode && (
        <Group spacing={8} mb={4}>
          <ActionIcon variant="transparent" onClick={() => navigate(-1)}>
            <IconArrowLeft />
          </ActionIcon>
          <Title fz={28} weight={600}>
            {t("VIEW_TRANSACTION_DETAILS")}
          </Title>
        </Group>
      )}
      <Space h={24} />
      <Center>
        <Card w={782} p={20} radius="md">
          {isLoanTx ? (
            <LoanResultHero
              state={loanState}
              message={loanMessage}
              amountText={loanAmountText}
            />
          ) : (
          <BackgroundImage src={background} radius="md" p={20}>
              <Stack align="center" spacing={10}>
                <Box w={!state?.mode ? 140 : 100} h={88}>
                  <Image src={getImgByType(state?.mode || "")} />
                </Box>
                {state?.type === "LOCK_ACCOUNT" && (
                  <Text c="#fff">{t("ACCOUNT_LOCK_REQUEST_APPROVED")}</Text>
                )}
                {state?.type === "SAVINGS" && (
                  <>
                    <Text c="#fff">
                      {/* #56050: truoc day luon ghi "da duoc chap nhan" ke ca khi tu choi. */}
                      {getSavingsBannerText(state?.mode)}
                    </Text>
                    {savingsBannerAmount && (
                      <Text c="#fff" fw={600} fz={20}>
                        {savingsBannerAmount}
                      </Text>
                    )}
                  </>
                )}
                {isTransfer && getStatusTextByMode(state?.mode || "") && (
                  <Text c="#fff" fw={600} fz={18}>
                    {getStatusTextByMode(state?.mode || "")}
                  </Text>
                )}
                {isBulk && (
                  <>
                    <Text c="#fff">{getBulkBannerText(state?.mode)}</Text>
                    <Text c="#fff" fw={800} fz={28}>
                      {formatCurrency(batchAmount)} VND
                    </Text>
                  </>
                )}
                {isOtherType && (
                  <Text c="#fff">
                    {getOtherBannerText(state?.mode, otherTransactionTypeLabel)}
                  </Text>
                )}
              </Stack>
            </BackgroundImage>
          )}
          <Stack spacing={12} mt={12}>
            {state?.type === "LOCK_ACCOUNT" && (
              <>
                <Flex justify="space-between">
                  <Text c="dimmed">{t("REFERENCE_ID")}</Text>
                  <Text fw={500} c="#121826">
                    {state?.dataConfirm?.approvalProcessInfo?.approvalProcessId}
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  <Text c="dimmed">{t("TRANSFER_TYPE")}</Text>
                  <Text fw={500} c="#121826">
                    {t("LOCK_ACCOUNT")}
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  <Text c="dimmed">{t("ACCOUNT_NICKNAME")}</Text>
                  <Text fw={500} c="#121826">
                    {state?.dataConfirm?.customerName}
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  <Text c="dimmed">{t("ACCOUNT_NUMBER")}</Text>
                  <Text fw={500} c="#121826">
                    {state?.dataConfirm?.accountNumber}
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  <Text c="dimmed">{t("AUTHORITY")}</Text>
                  <Text fw={500} c="#121826">
                    {state?.dataConfirm?.approvalProcessInfo?.numberOfApprovals}
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  <Text c="dimmed">{t("REVIEWER")}</Text>
                  <Text fw={500} c="#121826">
                    {
                      state?.dataConfirm?.approvalProcessInfo
                        ?.approvalUserNames[0]
                    }
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  <Text c="dimmed">{t("REQUEST_DATE_LABEL")}</Text>
                  <Text fw={500} c="#121826">
                    {state?.dataConfirm?.createdAt}
                  </Text>
                </Flex>
                <Flex gap={10}>
                  <Button
                    onClick={() => navigate(RoutesString.Dashboard)}
                    sx={() => ({
                      width: "50%",
                      backgroundColor: "#F4F4F6",
                      color: "#525866",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      cursor: "pointer",
                      fontWeight: 400,
                      "&:hover": {
                        backgroundColor: "#F4F4F6",
                      },
                    })}
                  >
                    {t("DASHBOARD")}
                  </Button>
                  <Button
                    onClick={() => navigate(RoutesString.Dashboard)}
                    type="submit"
                    sx={() => ({
                      width: "50%",
                      backgroundColor: "#2A81D0",
                      color: "white",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      cursor: "pointer",
                      fontWeight: 400,
                    })}
                  >
                    {t("CONFIRM")}
                  </Button>
                </Flex>
              </>
            )}

            {state?.type === "SAVINGS" && savingsDetailRows.length > 0 && (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <>
                  {savingsDetailRows.map((item, index) => (
                      <Flex justify="space-between" key={item?.key || index}>
                        {/* D12: nhãn hiển thị theo BE đã dịch (display/name), key chỉ để lookup */}
                        <Text c="dimmed">
                          {beText(item?.display || item?.name || item?.key)}
                        </Text>
                        <Text
                          fw={500}
                          c={loanRowColor(item?.display || item?.name || item?.key)}
                        >
                          {beText(item?.value)}
                        </Text>
                      </Flex>
                    ),
                  )}
                  <Flex gap={10}>
                    <Button
                      onClick={() => navigate(RoutesString.TransactionList)}
                      type="submit"
                      sx={() => ({
                        width: "50%",
                        backgroundColor: "#2A81D0",
                        color: "white",
                        borderRadius: "8px",
                        padding: "8px 10px",
                        cursor: "pointer",
                        fontWeight: 400,
                      })}
                    >
                      {t("LIST_TRACSACTIONS")}
                    </Button>
                    <Button
                      onClick={() => navigate(RoutesString.TransactionList)}
                      sx={() => ({
                        width: "50%",
                        backgroundColor: "#F4F4F6",
                        color: "#525866",
                        borderRadius: "8px",
                        padding: "8px 10px",
                        cursor: "pointer",
                        fontWeight: 400,
                        "&:hover": {
                          backgroundColor: "#F4F4F6",
                        },
                      })}
                    >
                      {t("DEPOSIT_LIST")}
                    </Button>
                  </Flex>
                  <Button
                    onClick={() => navigate(RoutesString.Dashboard)}
                    sx={() => ({
                      width: "100%",
                      backgroundColor: "#F4F4F6",
                      color: "#525866",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      cursor: "pointer",
                      fontWeight: 400,
                      "&:hover": {
                        backgroundColor: "#F4F4F6",
                      },
                    })}
                  >
                    {t("DASHBOARD")}
                  </Button>
                </>
              )}

            {/* Khoá tài khoản: tái dùng layout 7 field giống màn maker (LockDetailWrapper). */}
            {isCloseAccount && (
              <>
                <Flex justify="space-between">
                  <Text c="dimmed">{t("REFERENCE_ID")}</Text>
                  <Text fw={500} c="#121826">
                    {closeAccountData.reference}
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  <Text c="dimmed">{t("TRANSFER_TYPE")}</Text>
                  <Text fw={500} c="#121826">
                    {t("LOCK_ACCOUNT")}
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  <Text c="dimmed">{t("ACCOUNT_NICKNAME")}</Text>
                  <Text fw={500} c="#121826" ta="right">
                    {closeAccountData.accountName}
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  <Text c="dimmed">{t("ACCOUNT_NUMBER")}</Text>
                  <Text fw={500} c="#121826">
                    {closeAccountData.accountNumber}
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  <Text c="dimmed">{t("AUTHORITY")}</Text>
                  <Text fw={500} c="#121826">
                    {closeAccountData.level}
                  </Text>
                </Flex>
                {(state?.mode === "RETURN" || state?.mode === "REJECT") &&
                  state?.reason && (
                    <Flex justify="space-between">
                      <Text c="dimmed">{t("REASON")}</Text>
                      <Text fw={500} c="#121826" ta="right">
                        {state.reason}
                      </Text>
                    </Flex>
                  )}
                <Flex justify="space-between">
                  <Text c="dimmed">{t("REVIEWER")}</Text>
                  <Text fw={500} c="#121826" ta="right">
                    {closeAccountData.approvers}
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  <Text c="dimmed">{t("APPROVAL_DATE")}</Text>
                  <Text fw={500} c="#121826">
                    {closeAccountData.requestDate}
                  </Text>
                </Flex>
                <Flex gap={10} mt={4}>
                  <Button
                    onClick={() => navigate(RoutesString.Dashboard)}
                    sx={() => ({
                      width: "50%",
                      backgroundColor: "#F4F4F6",
                      color: "#525866",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      cursor: "pointer",
                      fontWeight: 400,
                      "&:hover": { backgroundColor: "#F4F4F6" },
                    })}
                  >
                    {t("DASHBOARD")}
                  </Button>
                  <Button
                    onClick={() => navigate(RoutesString.TransactionList)}
                    sx={() => ({
                      width: "50%",
                      backgroundColor: "#2A81D0",
                      color: "white",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      cursor: "pointer",
                      fontWeight: 400,
                    })}
                  >
                    {t("LIST_TRACSACTIONS")}
                  </Button>
                </Flex>
              </>
            )}

            {((isOtherType && !isCloseAccount) || isTransfer) && (
              <>
                {isLoanTx && displayRows.length > 0 && (
                  <Text fz={16} fw={700} c="#121826" mt={10}>
                    {t("TRANSFER_INFORMATION")}
                  </Text>
                )}
                {displayRows.length > 0 ? (
                  displayRows.map((item, index) => {
                    // Nhãn hiển thị ưu tiên name (key có thể là mã máy, vd "SUCCESS").
                    const label =
                      (item as { display?: string })?.display ||
                      (item as { name?: string })?.name ||
                      item?.key;
                    return (
                      <Flex
                        justify="space-between"
                        align="center"
                        // eslint-disable-next-line react/no-array-index-key
                        key={label || index}
                      >
                        <Text c="dimmed">{beText(label)}</Text>
                        {/* Design khoản vay hiện Trạng thái là chữ thường, không phải pill;
                            "Số tiền thanh toán" tô xanh cho khớp dòng tiền ở hero. */}
                        {isStatusKey(label) && !isLoanTx ? (
                          <LabelStatusPill label={beText(item?.value)} />
                        ) : (
                          <Text
                            fw={500}
                            ta="right"
                            // OP-55704: design tô ĐỎ riêng dòng lý do từ chối/hoàn lại; xanh cho
                            // "Số tiền thanh toán" để khớp con số ở hero; còn lại màu chữ thường.
                            c={loanRowColor(label)}
                          >
                            {beText(item?.value)}
                          </Text>
                        )}
                      </Flex>
                    );
                  })
                ) : (
                  <Text c="dimmed" ta="center" py={20}>
                    {t("NO_DETAILED_INFORMATION")}
                  </Text>
                )}

                {/* JVC-3534: khóa/mở khóa thẻ — bổ sung Người duyệt/Ngày duyệt (lấy từ log APPROVE). */}
                {isCardLockAction && state?.mode === "APPROVE" && (
                  <>
                    <Flex justify="space-between" align="center">
                      <Text c="dimmed">{t("REVIEWER")}</Text>
                      <Text fw={500} c="#121826">
                        {approverName}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text c="dimmed">{t("APPROVAL_DATE")}</Text>
                      <Text fw={500} c="#121826">
                        {approvalDate}
                      </Text>
                    </Flex>
                  </>
                )}

                {/* Danh sách file chứng từ — JVC-3520: GD thẻ không có chứng từ thì ẩn khối.
                    JVC-3645: màn kết quả chuyển tiền nội địa không đính kèm chứng từ → chỉ hiện
                    khối khi THỰC SỰ có file, bỏ dòng "Không có chứng từ" thừa. */}
                {documentInfoList.length > 0 && (
                <Box>
                  <Text fw={600} fz={14} c="#212936" mb={10}>
                    {t("DOCUMENT_FILE_LIST")}
                  </Text>
                  {documentInfoList.length === 0 ? (
                    <Text fz={14} c="#6B7280">
                      {t("NO_DOCUMENTS")}
                    </Text>
                  ) : (
                    <Stack spacing={12}>
                      {documentInfoList.map((doc, idx) => (
                        <Flex
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${doc.documentId ?? doc.documentName ?? ""}-${idx}`}
                          justify="space-between"
                          align="center"
                        >
                          <Flex gap={10} align="center">
                            <FileBadge
                              fileType={getFileExt(doc.documentName)}
                              size={30}
                            />
                            <Text fz={14} c="#157CC1">
                              {doc.documentName}
                            </Text>
                          </Flex>
                          <IconDownload
                            size={20}
                            color="#2A81D0"
                            style={{ cursor: "pointer" }}
                            onClick={() => openStoredDocument(doc.documentPath)}
                          />
                        </Flex>
                      ))}
                    </Stack>
                  )}
                </Box>
                )}

                <Flex gap={10}>
                  <Button
                    onClick={() => navigate(RoutesString.Dashboard)}
                    sx={() => ({
                      width: "50%",
                      backgroundColor: "#F4F4F6",
                      color: "#525866",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      cursor: "pointer",
                      fontWeight: 400,
                      "&:hover": {
                        backgroundColor: "#F4F4F6",
                      },
                    })}
                  >
                    {t("DASHBOARD")}
                  </Button>
                  <Button
                    onClick={() => navigate(RoutesString.TransactionList)}
                    sx={() => ({
                      width: "50%",
                      backgroundColor: "#2A81D0",
                      color: "white",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      cursor: "pointer",
                      fontWeight: 400,
                    })}
                  >
                    {t("LIST_TRACSACTIONS")}
                  </Button>
                </Flex>
              </>
            )}

            {isBulk && (
              <>
                {isFetchingDetail ? (
                  <Center py={32}>
                    <Loader />
                  </Center>
                ) : (
                  <>
                    <Row label={t("BATCH_ID")} value={txDetail?.batchNo || "-"} />
                    <Row label={t("TRANSFER_TYPE")} value={t("BATCH_TRANSFER")} />
                    <Row
                      label={t("CURRENT_ACCOUNT")}
                      value={txDetail?.fromAccountNo || "-"}
                    />
                    <Row
                      label={t("ACCOUNT_HOLDER")}
                      value={txDetail?.fromAccountFullName || "-"}
                    />
                    <Row
                      label={t("AMOUNT")}
                      value={
                        <Text span fw={700} c="#2A81D0">
                          {formatCurrency(principalAmount)} VND
                        </Text>
                      }
                    />
                    {(state?.mode === "RETURN" ||
                      state?.mode === "REJECT") &&
                      state?.reason && (
                        <Row label={t("REASON")} value={state.reason} />
                      )}
                    <Row
                      label={t("BATCH_STATUS")}
                      value={
                        <StatusPill
                          status={
                            state?.mode === "RETURN"
                              ? "WAITING_APPROVE"
                              : txDetail?.transStatus
                          }
                        />
                      }
                    />
                    <Row
                      label={t("CREATION_DATE")}
                      value={formatDateTime(createdDate)}
                    />
                    <Row
                      label={t("APPROVAL_DATE")}
                      value={formatDateTime(approveLog?.actionTime)}
                    />
                    <Row
                      label={t("REVIEWER")}
                      value={
                        approveLog?.approvalUserName ||
                        approveLog?.makerUserName ||
                        "-"
                      }
                    />
                  </>
                )}

                <Group position="center" mt={4}>
                  <Button
                    variant="subtle"
                    size="sm"
                    rightIcon={
                      showTable ? (
                        <IconChevronUp size={16} />
                      ) : (
                        <IconChevronDown size={16} />
                      )
                    }
                    onClick={() => setShowTable((v) => !v)}
                  >
                    {showTable ? t("COLLAPSE") : t("SHOW_MORE")}
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Card>
      </Center>

      {/* Bảng danh sách giao dịch + nút — vùng RỘNG riêng (rộng hơn card thông tin) */}
      {isBulk && (
        <Center>
          <Stack w="100%" maw={1400} px={16} spacing={16} mt={16}>
            {showTable && (
              <Card radius="md" p="lg">
                <Stack spacing={12}>
                  <Text fw={600} fz={16}>
                    {t("LIST_TRACSACTIONS")}
                  </Text>
                  {isFetchingBatch ? (
                    <Center py={20}>
                      <Loader />
                    </Center>
                  ) : (
                    <>
                      <ListOrder
                        orders={(batchItems || []) as any}
                        currentPage={batchPage}
                        pageSize={10}
                        showIndex={false}
                        showCode
                        statusVariant="actual"
                        actualMode={state?.mode}
                      />
                      {batchTotal > 0 && (
                        <Flex justify="flex-end">
                          <Pagination
                            total={batchPages}
                            value={batchPage}
                            onChange={setBatchPage}
                          />
                        </Flex>
                      )}
                      <Group position="apart" mt="xs">
                        <Text fz={14}>
                          {t("TOTAL_VALID_TRANSACTIONS_COLON")}{" "}
                          <Text span c="#16A34A" fw={700}>
                            {batchTotal}
                          </Text>
                        </Text>
                        <Text fz={14}>
                          {t("TOTAL_AMOUNT_VND_COLON")}{" "}
                          <Text span c="#2A81D0" fw={700} fz={18}>
                            {formatCurrency(principalAmount)} VND
                          </Text>
                        </Text>
                      </Group>
                    </>
                  )}
                </Stack>
              </Card>
            )}
            <Group position="center">
              <Button
                onClick={() => navigate(RoutesString.Dashboard)}
                styles={{
                  root: {
                    backgroundColor: "#fff",
                    color: NAVY,
                    border: `1px solid ${NAVY}`,
                    borderRadius: 8,
                  },
                }}
              >
                {t("BACK_TO_HOME")}
              </Button>
              <Button
                onClick={() => navigate(RoutesString.TransactionList)}
                styles={{
                  root: { backgroundColor: NAVY, color: "#fff", borderRadius: 8 },
                }}
              >
                {t("LIST_TRACSACTIONS")}
              </Button>
            </Group>
          </Stack>
        </Center>
      )}
    </>
  );
};

export default ApprovelLockAccountChecker;
